import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { AddToProviderPoolDialog } from "@/features/provider-pool/add-to-provider-pool-dialog";
import { MarkNotHiredDialog } from "@/features/affiliation/mark-not-hired-dialog";
import { useOrganization } from "@/hooks/use-organization";
import { CandidateNotesDialog } from "../candidate-notes-thread";
import type { JobDashboardActions } from "./use-job-dashboard-actions";

const DASHBOARD_DIALOG_CLASS_NAME =
  "!w-[calc(100vw-2rem)] !max-w-lg !max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6";
const DASHBOARD_DIALOG_FOOTER_CLASS_NAME =
  "mt-0 gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:space-x-0";

export function JobDashboardDialogs({
  jobId,
  user,
  actions,
}: {
  jobId: string;
  user: { id?: string; is_admin: boolean } | null | undefined;
  actions: JobDashboardActions;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const organizationBusinessUnitId = selectedOrgBusinessUnit.id;
  const {
    hireTarget,
    removeFromPoolTarget,
    rejectTarget,
    hireMutation,
    rejectCandidateMutation,
    removeFromPoolMutation,
  } = actions;

  return (
    <>
      {actions.confirmClose && (
        <Dialog
          open={actions.confirmClose}
          onOpenChange={actions.setConfirmClose}
        >
          <DialogContent className={DASHBOARD_DIALOG_CLASS_NAME}>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
                Close Job
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
              Are you sure you want to close this job posting? Candidates will
              no longer be able to apply. You can always re-open the job posting
              later.
            </DialogDescription>
            <DialogFooter className={DASHBOARD_DIALOG_FOOTER_CLASS_NAME}>
              <Button
                variant="outline"
                onClick={() => actions.setConfirmClose(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  actions.setOpenJobMutation.mutate({
                    id: jobId,
                    organizationBusinessUnitId,
                    open: false,
                  })
                }
                disabled={actions.setOpenJobMutation.isPending}
                variant="destructive"
              >
                {actions.setOpenJobMutation.isPending
                  ? "Closing..."
                  : "Close Job"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {actions.confirmDelete && (
        <Dialog
          open={actions.confirmDelete}
          onOpenChange={actions.setConfirmDelete}
        >
          <DialogContent className={DASHBOARD_DIALOG_CLASS_NAME}>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
                Delete Job Post
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
              Are you sure you want to permanently delete this job posting? This
              action cannot be undone. Only job posts that haven't been reviewed
              can be deleted.
            </DialogDescription>
            <DialogFooter className={DASHBOARD_DIALOG_FOOTER_CLASS_NAME}>
              <Button
                variant="outline"
                onClick={() => actions.setConfirmDelete(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => actions.deleteJobMutation.mutate({ id: jobId })}
                disabled={actions.deleteJobMutation.isPending}
                variant="destructive"
              >
                {actions.deleteJobMutation.isPending
                  ? "Deleting..."
                  : "Delete Job Post"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {rejectTarget && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              actions.closeReject();
            }
          }}
        >
          <DialogContent className={DASHBOARD_DIALOG_CLASS_NAME}>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
                Reject Candidate
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
              Are you sure you want to reject{" "}
              <span className="font-medium">{rejectTarget.name}</span> for this
              position? You can reinstate them later if needed.
            </DialogDescription>
            <div className="space-y-2">
              <Label
                htmlFor="reject-reason"
                className="text-sm font-medium text-slate-700 mb-1.5 block"
              >
                Internal reason (optional)
              </Label>
              <Textarea
                id="reject-reason"
                placeholder="Why are you rejecting this candidate? Visible to your team only."
                value={actions.rejectReason}
                onChange={(e) => actions.setRejectReason(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1.5">
                For internal use only — the candidate won't see this. Use the
                chat message below if you want to say something to them.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex min-h-12 items-center gap-3">
                <Checkbox
                  id="send-rejection-message"
                  checked={actions.sendRejectionMessage}
                  onCheckedChange={(checked) =>
                    actions.setSendRejectionMessage(checked === true)
                  }
                />
                <Label
                  htmlFor="send-rejection-message"
                  className="flex min-h-12 cursor-pointer items-center text-sm font-medium leading-5 text-slate-700"
                >
                  Send a chat message to the candidate
                </Label>
              </div>
              {actions.sendRejectionMessage && (
                <Textarea
                  placeholder="Write a message to the candidate..."
                  value={actions.rejectionMessage}
                  onChange={(e) => actions.setRejectionMessage(e.target.value)}
                  aria-label="Chat message to the candidate"
                  rows={5}
                />
              )}
            </div>
            <DialogFooter className={DASHBOARD_DIALOG_FOOTER_CLASS_NAME}>
              <Button variant="outline" onClick={actions.closeReject}>
                Cancel
              </Button>
              <Button
                disabled={rejectCandidateMutation.isPending}
                onClick={() => {
                  const message = actions.rejectionMessage.trim();
                  rejectCandidateMutation.mutate({
                    userId: rejectTarget.userId,
                    reason: actions.rejectReason || undefined,
                    message:
                      actions.sendRejectionMessage && message
                        ? message
                        : undefined,
                  });
                }}
                variant="destructive"
              >
                {rejectCandidateMutation.isPending
                  ? "Rejecting..."
                  : "Reject Candidate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {actions.showProfileUserId && (
        <ProfileModal
          userId={actions.showProfileUserId}
          onClose={actions.closeProfile}
          organizationBusinessUnitId={organizationBusinessUnitId}
        />
      )}
      {actions.chatTarget && (
        <ChatDialog
          isOpen
          onOpenChange={actions.closeChat}
          user={actions.chatTarget}
        />
      )}
      {actions.notesTarget && user?.id && (
        <CandidateNotesDialog
          candidate={actions.notesTarget}
          organizationBusinessUnitId={organizationBusinessUnitId}
          currentUserId={user.id}
          isAdmin={user.is_admin}
          onClose={actions.closeNotes}
        />
      )}
      {actions.addToPoolUserId && (
        <AddToProviderPoolDialog
          isOpen
          onOpenChange={actions.closeAddToPool}
          onSuccess={actions.invalidateCandidates}
          userId={actions.addToPoolUserId}
        />
      )}

      <Dialog
        open={hireTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            actions.closeHire();
          }
        }}
      >
        <DialogContent className={DASHBOARD_DIALOG_CLASS_NAME}>
          <DialogHeader className="pr-6 text-left">
            <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
              Hire {hireTarget?.name ?? "Candidate"}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6 text-slate-600">
              Record this candidate as hired, with an optional start date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="hire-start-date" className="text-sm font-medium">
                Start Date (optional)
              </Label>
              <Input
                id="hire-start-date"
                type="date"
                value={actions.hireStartDate}
                onChange={(e) => actions.setHireStartDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className={DASHBOARD_DIALOG_FOOTER_CLASS_NAME}>
            <Button
              variant="outline"
              onClick={actions.closeHire}
              disabled={hireMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              disabled={hireMutation.isPending}
              onClick={() => {
                if (!hireTarget) {
                  return;
                }
                hireMutation.mutate({
                  organizationBusinessUnitId,
                  jobPostId: jobId,
                  userId: hireTarget.userId,
                  ...(actions.hireStartDate
                    ? { startDate: actions.hireStartDate }
                    : {}),
                });
              }}
            >
              {hireMutation.isPending ? "Hiring..." : "Confirm Hire"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MarkNotHiredDialog
        open={actions.markNotHiredTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            actions.closeMarkNotHired();
          }
        }}
        providerName={actions.markNotHiredTarget?.name}
        onCancel={actions.closeMarkNotHired}
        isPending={actions.markNotHiredMutation.isPending}
        onConfirm={() => {
          if (!actions.markNotHiredTarget) {
            return;
          }
          actions.markNotHiredMutation.mutate({
            organizationBusinessUnitId,
            userId: actions.markNotHiredTarget.userId,
          });
        }}
      />

      {removeFromPoolTarget && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              actions.closeRemoveFromPool();
            }
          }}
        >
          <DialogContent className={DASHBOARD_DIALOG_CLASS_NAME}>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
                Remove from Pool
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
              Are you sure you want to remove{" "}
              <span className="font-medium">
                {removeFromPoolTarget.candidateName}
              </span>{" "}
              from the{" "}
              <span className="font-medium">
                {removeFromPoolTarget.poolName}
              </span>{" "}
              pool?
            </DialogDescription>
            <DialogFooter className={DASHBOARD_DIALOG_FOOTER_CLASS_NAME}>
              <Button variant="outline" onClick={actions.closeRemoveFromPool}>
                Cancel
              </Button>
              <Button
                disabled={removeFromPoolMutation.isPending}
                onClick={() =>
                  removeFromPoolMutation.mutate({
                    providerPoolId: removeFromPoolTarget.providerPoolId,
                    userId: removeFromPoolTarget.userId,
                    organizationBusinessUnitId,
                  })
                }
                variant="destructive"
              >
                {removeFromPoolMutation.isPending ? "Removing..." : "Remove"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
